import { View } from "@novorender/api";
import { md5 } from "js-md5";

import { BackendProvider } from "config/backend";
import { hslToVec, VecRGBA } from "utils/color";
import { digestMessageWithSHA256ToHex } from "utils/misc";

export function getPropertyTreeColor(index: number) {
    return propertyTreeColors[index % propertyTreeColors.length];
}

const propertyTreeColors = createColors(12);

function createColors(count: number): VecRGBA[] {
    const increment = Math.floor(360 / count);
    return Array.from({ length: count }).map((_, i) => {
        return [...hslToVec(i * increment, 1, 0.5), 1];
    });
}

const characterMap = [
    // azcopy automatically decodes %22 into " when uploading to blob storage
    // (see https://github.com/Azure/azure-storage-azcopy/issues/916).
    // Instead of passing flag to azcopy to disable auto-decoding, we'll just not encode " on the client side
    // to avoid rebuilding all the projects
    // ['"', "%22"],
    ["<", "%3c"],
    [">", "%3e"],
    ["|", "%7c"],
    [":", "%3a"],
    ["*", "%2a"],
    ["?", "%3f"],
    ["\\", "%5c"],
    ["/", "%2f"],
    ["=", "%3d"],
    ["+", "%2b"],
    [" ", "+"],
    ["%", "%25"],
];

function encodePropertyPath(_path: string): string {
    let path = _path;

    for (const [invalid, encoded] of characterMap) {
        path = path.replaceAll(invalid, encoded);
    }

    return path.toLowerCase();
}

export async function getPropCacheUrl({
    view,
    path,
    pipelineVersion,
    backend,
}: {
    view: View;
    path: string;
    pipelineVersion: string | undefined;
    backend: BackendProvider;
}) {
    let useSha256 = false;
    if (backend === "procore") {
        // Procore backend always uses SHA-256
        useSha256 = true;
    } else if (pipelineVersion) {
        const [major, minor] = pipelineVersion.split(".").map(Number);
        const isOldPipelineWithNewHashing = major === 1 && minor >= 7;
        const isNewPipelineWithNewHashing = (major === 4 && minor >= 2) || major > 4;
        if (isOldPipelineWithNewHashing || isNewPipelineWithNewHashing) {
            useSha256 = true;
        }
    }

    if (useSha256) {
        if (path !== "root") {
            path = await digestMessageWithSHA256ToHex(path.toLowerCase());
        }
    } else {
        if (path.length > 128) {
            path = md5(path.toLowerCase());
        } else {
            path = encodePropertyPath(path);
        }
    }

    return view.buildAssetUrl(`propcache/${path}`);
}
